/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {RadioGroup} from "@headlessui/react";
import clsx from "clsx";
import {CheckIcon} from "@heroicons/react/24/solid";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";

interface BookingPackageItemsWithKartingProps {
    setWithKarting: (val: number) => void;
    withKarting: number | undefined;
    className?: string;
    kartingIsNotAvailable?: boolean;
}

const BookingPackageItemsWithKarting = (props: BookingPackageItemsWithKartingProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const settings = useTypedSelector(state => state.settingsState);
    const [withKarting, setWithKarting] = useState<number | undefined>(undefined);
    const [isKartingDisabled, setIsKartingDisabled] = useState<boolean>(!!settings?.config?.disable_karting_tickets || false);

    const kartingOptions = [
        //here go the options, with values from props
        {name: t("without Karting"), enabled: true, value: 0},
        {name: t("with Karting"), enabled: !isKartingDisabled && !props?.kartingIsNotAvailable, value: 1},
    ];

    const setKartingOptionHandler = (option: number) => {
        setWithKarting(option);
        props.setWithKarting(option);
    };

    useEffect(() => {
        setWithKarting(props.withKarting !== undefined ? +props.withKarting : undefined);
    }, [props.withKarting]);

    useEffect(() => {
        setIsKartingDisabled(!!settings?.config?.disable_karting_tickets || false);
    }, [settings?.config?.disable_karting_tickets]);

    // useEffect(() => {
    //     console.log("kartingIsNotAvailable", props?.kartingIsNotAvailable);
    // }, [props?.kartingIsNotAvailable]);

    const colorClasses = {
        "red-bg": "bg-arbo-red",
        "yellow-bg": "bg-arbo-yellow",
        "red-all": "bg-arbo-red border-arbo-red",
        "yellow-all": "bg-arbo-yellow border-arbo-yellow",
        "black-all": "bg-gray-900 border-gray-900",
        "black-bg": "bg-gray-900",
        "top-black":
            "hover:bg-gray-900 data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-gray-900 data-[checked]:ring-gray-900 data-[checked]:text-gray-900",
        "top-red":
            "hover:arbo-red data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-arbo-red data-[checked]:ring-arbo-red data-[checked]:text-arbo-red",
        "top-yellow":
            "hover:arbo-yellow data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-arbo-yellow data-[checked]:ring-arbo-yellow data-[checked]:text-arbo-yellow",
    };

    return (
        <div className={clsx("BookingPackageItemsWithKarting flex w-full", props.className)}>
            <fieldset aria-label={t("Choose a karting option")} className={"w-full sm:w-auto"}>
                <RadioGroup
                    className="flex flex-col items-center space-y-2 sm:space-y-0 w-full sm:w-auto sm:grid sm:grid-cols-2 sm:gap-2 xl:gap-3"
                    defaultValue={withKarting}
                    value={withKarting}
                    onChange={setKartingOptionHandler}
                >
                    {kartingOptions.map(option => {
                        const colorClass = option.value === 1 ? "red" : "yellow";
                        return (
                            <RadioGroup.Option
                                key={option.name}
                                className={clsx(
                                    option.enabled ? "cursor-pointer focus:outline-none" : "cursor-not-allowed opacity-25",
                                    "w-full lg:w-auto max-w-[260px] sm:max-w-full sm:min-h-[46px] xl:min-h-[50px] h-full relative flex flex-row items-center justify-center rounded-md pb-0 font-medium text-gray-600 border-2 border-gray-300 bg-transparent shadow-sm duration-150 transition-all ring-1 ring-transparent delay-0 ",
                                    colorClass === "yellow" ? colorClasses["top-yellow"] : colorClasses["top-red"]
                                )}
                                disabled={!option.enabled}
                                value={option.value}
                                {...(withKarting == option.value ? {"data-checked": true} : {})}
                            >
                                {({checked}) => (
                                    <div
                                        className={clsx(
                                            "relative overflow-hidden break-none flex flex-row justify-between items-center w-full h-full",
                                            checked ? (colorClass === "yellow" ? colorClasses["yellow-bg"] : colorClasses["red-bg"]) : "bg-gray-300"
                                        )}
                                    >
                                        <div
                                            className={clsx(
                                                "px-2.5 py-2 xl:px-2.5 xl:py-3 border-r h-full flex items-center justify-center",
                                                checked
                                                    ? colorClass === "yellow"
                                                        ? colorClasses["yellow-all"]
                                                        : colorClasses["red-all"]
                                                    : "bg-gray-300 border-gray-300"
                                            )}
                                        >
                                            <div
                                                className={clsx(
                                                    "w-4 h-4 xl:w-5 xl:h-5 border flex justify-center items-center rounded-full",
                                                    checked
                                                        ? colorClass === "yellow"
                                                            ? colorClasses["yellow-all"]
                                                            : colorClasses["red-all"]
                                                        : "border-gray-300 bg-white"
                                                )}
                                            >
                                                {checked && <CheckIcon className={"w-5 h-5 flex-none text-white"} />}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                "px-2 py-2 xl:px-4 xl:py-3 text-sm xl:text-base h-full w-full rounded-md flex items-center justify-center bg-white"
                                            }
                                        >
                                            {option.name}
                                        </div>
                                    </div>
                                )}
                            </RadioGroup.Option>
                        );
                    })}
                </RadioGroup>
            </fieldset>
        </div>
    );
};

export default BookingPackageItemsWithKarting;
